import React, { useState } from 'react';
import { Button, Card, CardBody, CardTitle, Input, Alert } from 'reactstrap';
import axios from 'axios';

const CouponVerification = ({ onVerificationSuccess }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';
  const [couponCode, setCouponCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    console.log('handleVerify called', { couponCode, verifying });
    
    if (!couponCode || couponCode.trim().length === 0) {
      console.log('Validation failed: empty coupon code');
      setError('Please enter a coupon code');
      return;
    }

    try {
      setVerifying(true);
      setError(null);
      setResult(null);

      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.error('No access token found in localStorage');
        setError('Authentication required. Please log in again.');
        setVerifying(false);
        return;
      }
      
      console.log('Making API call', {
        url: `${API_BASE_URL}/api/coupon/verify`,
        couponCode: couponCode.trim(),
        hasToken: !!token
      });

      const response = await axios.post(
        `${API_BASE_URL}/api/coupon/verify`,
        {
          couponCode: couponCode.trim() // Backend will get verifiedBy from JWT token
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Log response for debugging
      console.log('Coupon verification response:', response.data);
      
      setResult(response.data);
      
      // Check if verification was successful
      if (response.data.valid && response.data.verified && !response.data.alreadyVerified) {
        if (onVerificationSuccess) {
          onVerificationSuccess();
        }
      } else if (response.data.valid && response.data.alreadyVerified) {
        // Already verified - this is not an error, just informational
        console.log('Coupon already verified');
      } else if (!response.data.valid) {
        // Invalid response structure
        setError(response.data.error || 'Invalid response from server');
        setResult(null);
      }
    } catch (err) {
      console.error('Coupon verification error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to verify coupon';
      setError(errorMessage);
      setResult(null);
    } finally {
      setVerifying(false);
    }
  };

  const handleClear = () => {
    setCouponCode('');
    setResult(null);
    setError(null);
  };

  return (
    <Card className="shadow-sm border-0 mb-4">
      <CardBody>
        <CardTitle tag="h5" className="mb-3">Verify Coupon Code</CardTitle>
        
        <form onSubmit={(e) => {
          console.log('Form onSubmit triggered', { couponCode, verifying });
          handleVerify(e);
        }}>
          <div className="row align-items-end">
            <div className="col-md-8">
              <label className="form-label">Enter Coupon Code</label>
              <Input
                type="text"
                className="form-control form-control-lg"
                placeholder="e.g., A3X9"
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setResult(null);
                  setError(null);
                }}
                maxLength={4}
                disabled={verifying}
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.5rem',
                  textAlign: 'center',
                  textTransform: 'none' // Display code as stored (can be lowercase)
                }}
              />
            </div>
            <div className="col-md-4">
              <Button
                type="submit"
                color="primary"
                size="lg"
                block
                disabled={verifying || !couponCode.trim()}
                style={{ height: '50px' }}
              >
                {verifying ? 'Verifying...' : 'Verify Code'}
              </Button>
            </div>
          </div>
        </form>

        {error && (
          <Alert color="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {result && (
          <div className="mt-3">
            {result.valid && result.verified && !result.alreadyVerified && (
              <Alert color="success">
                <strong>✓ Verified Successfully!</strong><br />
                Code: <strong>{result.couponCode}</strong><br />
                User: {result.userName || result.userEmail}<br />
                Redeemed: {new Date(result.redeemedAt).toLocaleString()}<br />
                Verified: {new Date(result.verifiedAt).toLocaleString()}
              </Alert>
            )}
            
            {result.valid && result.alreadyVerified && (
              <Alert color="danger">
                <strong>Already Verified</strong><br />
                Code: <strong>{result.couponCode}</strong><br />
                User: {result.userName || result.userEmail}<br />
                Redeemed: {new Date(result.redeemedAt).toLocaleString()}<br />
                Verified: {new Date(result.verifiedAt).toLocaleString()}
              </Alert>
            )}
            
            {result.valid && (
              <Button color="secondary" size="sm" onClick={handleClear} className="mt-2">
                Clear & Verify Another
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default CouponVerification;
